import { Outlet, useLocation, useNavigation } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils.ts'
import Navigation from '@/components/common/menu/Navigation.tsx'
import BottomTabBar from '@/components/common/menu/BottomTabBar.tsx'
import MobileFab from '@/components/common/menu/MobileFab.tsx'
import LoadingIndicator from '@/components/common/LoadingIndicator.tsx'
import SuspenseWrapper from '@/components/common/wrappers/SuspenseWrapper.tsx'
import PrefetchProgressBar from '@/components/common/PrefetchProgressBar.tsx'
import OfflineBanner from '@/components/common/OfflineBanner.tsx'
import SlowConnectionBanner from '@/components/common/SlowConnectionBanner'
import BackgroundSyncBanner, {
  useIsBackgroundSyncing,
} from '@/components/common/BackgroundSyncBanner.tsx'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { useNativeOnlineStatus } from '@/lib/hooks/useNativeOnlineStatus'
import { useOfflineModeStore } from '@/store/useOfflineModeStore'
import { useSlowConnectionBannerStore } from '@/store/useSlowConnectionBannerStore'
import { usePullToRefresh } from '@/lib/hooks/usePullToRefresh'
import PullToRefreshIndicator from '@/components/common/PullToRefreshIndicator'
import { usePrefetchStore } from '@/store/usePrefetchStore'
import { getTopBannerContentOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

export default function RootLayout() {
  const isNavigating = useNavigation().state === 'loading'
  const isNative = isNativePlatform()
  const isOnline = useNativeOnlineStatus()
  const isOfflineModeEnabled = useOfflineModeStore(s => s.isOfflineModeEnabled)
  const isSlowBannerVisible = useSlowConnectionBannerStore(s => s.isVisible)
  const prefetchPhase = usePrefetchStore(s => s.phase)
  const isPrefetchBannerVisible = usePrefetchStore(s => s.isVisible)
  const showOfflineBanner = (isNative && !isOnline) || (isOnline && isOfflineModeEnabled)
  const showStatusBanner = showOfflineBanner || isSlowBannerVisible
  // Mirrors PrefetchProgressBar's own render gate — must stay in sync, else
  // the offset reserves a banner's worth of space for a banner that's not
  // actually rendering (phantom gap between topbar and content).
  const showPrefetchBanner =
    isPrefetchBannerVisible && prefetchPhase !== 'idle' && prefetchPhase !== 'cancelled'
  const showBackgroundSyncBanner = useIsBackgroundSyncing()
  const topBannerOffsetPx = getTopBannerContentOffsetPx({
    hasStatusBanner: showStatusBanner,
    hasPrefetchBanner: showPrefetchBanner,
  })
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const {
    state: pullState,
    contentRef,
    indicatorRef,
    iconWrapperRef,
    handlers,
  } = usePullToRefresh({
    enabled: isNative,
    scrollContainerRef,
  })

  const { pathname } = useLocation()
  useEffect(() => {
    if (pathname) {
      scrollContainerRef.current?.scrollTo(0, 0)
    }
  }, [pathname])

  // Expose `--top-banners-offset` on :root so both <Navigation> (mobile
  // topbar) and <main> can read it. Without this, the var set inline on
  // <main> doesn't reach the topbar, which lives in a sibling subtree.
  useEffect(() => {
    document.documentElement.style.setProperty('--top-banners-offset', `${topBannerOffsetPx}px`)
    return () => {
      document.documentElement.style.removeProperty('--top-banners-offset')
    }
  }, [topBannerOffsetPx])

  const mainContent = (
    <SuspenseWrapper type={`nested`} className={cn(isNavigating && 'hidden')}>
      <div className={cn(`grow`, isNavigating && 'opacity-50')}>
        <Outlet />
      </div>
    </SuspenseWrapper>
  )

  return (
    <>
      <Navigation />
      <PrefetchProgressBar bannerIndex={showStatusBanner ? 1 : 0} />
      <BackgroundSyncBanner
        visible={showBackgroundSyncBanner}
        bannerIndex={Number(showStatusBanner) + Number(showPrefetchBanner)}
      />
      <OfflineBanner />
      <SlowConnectionBanner />
      <main
        className={cn(
          'relative flex flex-col pt-[calc(var(--mobileToBarHeight)+var(--safe-area-inset-top)+var(--top-banners-offset,0px))]',
          'pb-[calc(var(--bottomTabBarHeight)+var(--safe-area-inset-bottom))]',
          isNative ? 'h-svh' : 'min-h-svh',
          'md:ml-[var(--sidebar-width)] md:min-h-svh md:pb-0 md:pt-[var(--top-banners-offset,0px)]',
          isNative && 'md:h-auto',
        )}
        style={
          {
            '--top-banners-offset': `${topBannerOffsetPx}px`,
          } as CSSProperties
        }
      >
        {isNative ? (
          <div
            ref={scrollContainerRef}
            className="flex flex-1 flex-col overflow-y-auto md:overflow-visible"
            {...handlers}
          >
            <PullToRefreshIndicator
              state={pullState}
              contentRef={contentRef}
              indicatorRef={indicatorRef}
              iconWrapperRef={iconWrapperRef}
            >
              {mainContent}
            </PullToRefreshIndicator>
          </div>
        ) : (
          mainContent
        )}
      </main>
      <BottomTabBar />
      <MobileFab />
      {isNavigating && <LoadingIndicator type="nested" />}
      {/* EnvironmentTag was a fixed bottom-right pill showing
          "D - <version>" / "S - <version>" — useful when the app had no
          other version surface, but now the build version is visible in
          the settings drawer footer (AppVersion). The floating pill was
          obscuring content in the bottom-right corner (sat on top of the
          mobile FAB and bottom tab bar) and duplicated info, so it's
          dropped. Re-mount here if a per-env color signal is needed. */}
    </>
  )
}
